import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { MerchantType } from '../models/onboarding.model';

export interface DashboardKPIs {
  // KPIs pour commerce fixe
  totalServices?: number;
  servicesDiff?: number;
  averagePrecision: number;
  precisionDiff?: number;
  wasteReduction: number;
  wasteReductionDiff?: number;
  totalRevenue: number;
  revenueGrowth?: number;
  totalSavings: number;
  savingsGrowth?: number;
  
  // KPIs pour commerce itinérant
  totalEvents?: number;
  upcomingEvents?: number;
  pendingEvents?: number;
}

export interface DailyPredictions {
  date: Date;
  weather?: {
    condition: string;
    temperature: number;
    icon: string;
  };
  affluence: 'low' | 'medium' | 'high';
  services: ServicePrediction[];
}

export interface ServicePrediction {
  name: string;
  timeRange: string;
  products: ProductPrediction[];
}

export interface ProductPrediction {
  name: string;
  quantity: number;
  unit: string;
  range: { low: number; high: number };
}

export interface WeeklyTrends {
  days: DayTrend[];
}

export interface DayTrend {
  name: string;
  date: Date;
  precision: number;
  revenue: number;
}

export interface EventPerformance {
  eventType: string;
  icon: string;
  name: string;
  count: number;
  precision: number;
  averageRevenue: number;
}

export interface Action {
  id: string;
  type: 'sales_input' | 'stock_adjustment' | 'event_preparation';
  icon: string;
  text: string;
  date?: Date;
  eventId?: string;
  priority: 'low' | 'medium' | 'high';
}

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private apiUrl = `${environment.apiUrl}/dashboard`;

  constructor(private http: HttpClient) {}

  // KPIs communs
  getKPIs(merchantType: MerchantType): Observable<DashboardKPIs> {
    return this.http.get<DashboardKPIs>(`${this.apiUrl}/kpis`, {
      params: { merchantType },
    });
  }

  // Actions en attente
  getPendingActions(): Observable<Action[]> {
    return this.http.get<Action[]>(`${this.apiUrl}/actions/pending`);
  }

  // === FIXE ===

  getDailyPredictions(date: Date): Observable<DailyPredictions> {
    const dateStr = date.toISOString().split('T')[0];
    return this.http.get<DailyPredictions>(
      `${this.apiUrl}/fixed/predictions/daily`,
      {
        params: { date: dateStr },
      }
    );
  }

  getWeeklyTrends(): Observable<WeeklyTrends> {
    return this.http.get<WeeklyTrends>(`${this.apiUrl}/fixed/trends/weekly`);
  }

  getServiceSchedule(date: Date): Observable<ServicePrediction[]> {
    const dateStr = date.toISOString().split('T')[0];
    return this.http.get<ServicePrediction[]>(`${this.apiUrl}/fixed/schedule`, {
      params: { date: dateStr },
    });
  }

  // === ITINÉRANT ===

  getUpcomingEvents(limit: number = 10): Observable<any> {
    return this.http.get(`${this.apiUrl}/itinerant/events/upcoming`, {
      params: { limit: limit.toString() },
    });
  }

  getEventPerformance(): Observable<EventPerformance[]> {
    return this.http.get<EventPerformance[]>(
      `${this.apiUrl}/itinerant/performance/by-type`
    );
  }

  getLocationStats(): Observable<any> {
    return this.http.get(`${this.apiUrl}/itinerant/stats/locations`);
  }
}
